import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Home, Sparkles, Copy, ArrowRight, Zap } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImportListing = ({ user }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [rawText, setRawText] = useState("");
  const [parsedData, setParsedData] = useState(null);
  const [loading, setLoading] = useState(false);

  const exampleText = `Beautiful 4BR/3BA home in Frisco! $425,000
3 bed 2.5 bath, 2,400 sqft
123 Oak Street, Frisco TX 75034
Updated kitchen, hardwood floors, large backyard with pool
Built 2015, great schools, quiet neighborhood
Call John at 555-123-4567`;

  const parseListingText = async () => {
    if (!rawText.trim()) {
      toast.error("Please paste your listing text");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/listings/parse-import`,
        { raw_text: rawText },
        { withCredentials: true }
      );
      
      setParsedData(response.data);
      setStep(2);
      toast.success("Listing parsed! Review and enhance below 🎉");
    } catch (error) {
      toast.error("Failed to parse listing");
    } finally {
      setLoading(false);
    }
  };

  const createFromParsed = () => {
    // Navigate to create listing with pre-filled data
    navigate('/create', { state: { importedData: parsedData } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-slate-50">
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-3 bg-purple-100 text-purple-700 px-6 py-3 rounded-full mb-6">
            <Sparkles className="w-5 h-5" />
            <span className="font-semibold">AI-Powered Listing Import</span>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Migrate Your Listing in 60 Seconds
          </h1>
          <p className="text-xl text-gray-600">
            Already posted on Facebook Marketplace or elsewhere? Copy, paste, and we'll enhance it with AI!
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card className={`p-8 ${step === 1 ? 'border-4 border-orange-500' : 'border-2 border-gray-200 opacity-50'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Step 1: Paste</h3>
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">
                1
              </div>
            </div>
            <p className="text-gray-600 mb-4">Copy your existing listing from Facebook, Zillow, or anywhere</p>
          </Card>

          <Card className={`p-8 ${step === 2 ? 'border-4 border-orange-500' : 'border-2 border-gray-200 opacity-50'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Step 2: Enhance</h3>
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">
                2
              </div>
            </div>
            <p className="text-gray-600 mb-4">AI extracts details and adds 40+ missing data points</p>
          </Card>
        </div>

        {step === 1 && (
          <Card className="p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Paste Your Listing Text</h3>
            
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">Your listing text from any platform:</label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setRawText(exampleText)}
                  className="text-blue-600"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Try Example
                </Button>
              </div>
              <Textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder="Paste your listing here... Include price, address, beds/baths, description, etc."
                rows={12}
                className="text-base"
                data-testid="import-textarea"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-blue-900 mb-2">💡 What We'll Extract:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Price, address, beds/baths, square footage</li>
                <li>• Property features and description</li>
                <li>• Contact information</li>
                <li>• City, county, ZIP code</li>
              </ul>
            </div>

            <Button
              onClick={parseListingText}
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white text-lg py-6"
              data-testid="parse-listing-btn"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white mr-3"></div>
                  Parsing with AI...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Parse & Enhance with AI
                </>
              )}
            </Button>
          </Card>
        )}

        {step === 2 && parsedData && (
          <Card className="p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">✨ AI-Enhanced Preview</h3>
              <div className="flex items-center space-x-2 bg-green-100 text-green-700 px-4 py-2 rounded-full">
                <Zap className="w-4 h-4" />
                <span className="font-semibold">Extracted {Object.keys(parsedData).length} fields</span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Extracted Data:</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  {parsedData.title && <div><span className="font-medium">Title:</span> {parsedData.title}</div>}
                  {parsedData.price && <div><span className="font-medium">Price:</span> ${parsedData.price.toLocaleString()}</div>}
                  {parsedData.address && <div><span className="font-medium">Address:</span> {parsedData.address}</div>}
                  {parsedData.city && <div><span className="font-medium">City:</span> {parsedData.city}</div>}
                  {parsedData.bedrooms && <div><span className="font-medium">Beds:</span> {parsedData.bedrooms}</div>}
                  {parsedData.bathrooms && <div><span className="font-medium">Baths:</span> {parsedData.bathrooms}</div>}
                  {parsedData.sqft && <div><span className="font-medium">Sqft:</span> {parsedData.sqft.toLocaleString()}</div>}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">What's Next:</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">AI Description</p>
                      <p className="text-sm text-gray-600">Professional rewrite included</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Add 40+ Details</p>
                      <p className="text-sm text-gray-600">HVAC, roof, utilities, etc.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Free Media Kit</p>
                      <p className="text-sm text-gray-600">Social posts, flyers, emails</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1"
              >
                Back to Edit
              </Button>
              <Button
                onClick={createFromParsed}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white text-lg py-6"
                data-testid="continue-to-create-btn"
              >
                Continue to Full Listing Form
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {/* Why Migrate Section */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          <Card className="p-6 border-2 border-green-200 bg-green-50">
            <h4 className="text-xl font-bold text-gray-900 mb-3">10x More Details</h4>
            <p className="text-gray-700">
              Facebook allows ~10 fields. We capture 50+ including HVAC age, roof warranty, mineral rights, surveys, and more.
            </p>
          </Card>
          <Card className="p-6 border-2 border-blue-200 bg-blue-50">
            <h4 className="text-xl font-bold text-gray-900 mb-3">Serious Buyers Only</h4>
            <p className="text-gray-700">
              Our platform attracts motivated buyers who appreciate comprehensive property information - no tire kickers.
            </p>
          </Card>
          <Card className="p-6 border-2 border-purple-200 bg-purple-50">
            <h4 className="text-xl font-bold text-gray-900 mb-3">Free Forever</h4>
            <p className="text-gray-700">
              List for 90 days free, renew for $29/mo. Facebook can remove posts anytime. You control your listing here.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ImportListing;
